<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import VContentBox from '@shared/component/VContentBox/VContentBox.vue';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { type PolisServiceCtConditie } from '@services/polis-public/orval-api/model/polisServiceCtConditie';
import { usePolisGetPolissen } from '@services/polis-public/orval-api/polis-api/polis-api';
import { usePolisDetails } from '../usePolisDetails';

type VerzekeringMetVoorwaardens = {
  conditie?: PolisServiceCtConditie[] | null;
  dekking?: Array<{ conditie?: PolisServiceCtConditie[] | null }> | null;
};

const props = defineProps<{
  polisnummer: string;
  relatienummer: number;
}>();

const { polisnummer, relatienummer } = toRefs(props);

const { isPending: polissenPending, data: polissen } = usePolisGetPolissen(
  computed(() => ({
    relatienummer: relatienummer.value,
    neemRoyementenMee: true,
    neemBeursPakkettenMee: true,
  })),
  {
    query: {
      select: (result) => result.result,
      staleTime: 5 * 60 * 1000,
    },
  },
);

const pakketpolis = computed(() => {
  if (polissenPending.value || !polissen.value) return undefined;

  return polissen.value.find(
    (polis) =>
      polis.polisnummer === polisnummer.value ||
      polis.subpolissen?.some((sub) => sub.polisnummer === polisnummer.value),
  );
});

const pakketpolisnummer = computed(() => pakketpolis.value?.polisnummer ?? '');

const { data: pakketpolisDetails, isPending: pakketpolisDetailsPending } = usePolisDetails(
  pakketpolisnummer,
  { enabled: computed(() => !!pakketpolisnummer.value) },
);
const { data, isPending: polisDetailsPending } = usePolisDetails(polisnummer);

const subpolis = computed(() =>
  data?.value?.polis?.subpolis?.length ? data.value.polis.subpolis[0] : undefined,
);

const verzekering = computed(() => {
  if (!subpolis.value || !data.value?.polis?.soortverzekering) return undefined;

  const verzekeringen = subpolis.value as unknown as Record<
    string,
    VerzekeringMetVoorwaardens[] | undefined
  >;

  return verzekeringen[data.value.polis.soortverzekering.toLowerCase()]?.[0];
});

const condities = computed(() => [
  ...(data?.value?.polis?.conditie ?? []),
  ...(pakketpolisDetails.value?.polis?.conditie ?? []),
  ...(subpolis.value?.conditie ?? []),
  ...(verzekering.value?.conditie ?? []),
  ...(verzekering.value?.dekking?.flatMap((d) => d.conditie ?? []) ?? []),
]);

const uniekeCondities = computed(
  () =>
    new Set(
      condities.value
        .map((d) => d.internconditienummer)
        .filter((nummer): nummer is string => !!nummer),
    ),
);
</script>
<template>
  <div class="flex flex-col gap-4">
    <VSkeleton
      v-if="polissenPending || polisDetailsPending || pakketpolisDetailsPending"
      height="xl"
    />
    <VInfoBox v-else-if="!condities.length" type="info">
      Deze subpolis heeft geen voorwaarden.
    </VInfoBox>
    <div v-else class="flex flex-col gap-4">
      <a
        v-for="conditie in uniekeCondities"
        :key="conditie"
        :href="`/document/push/?voorwaardeid=${conditie}`"
        target="_blank"
        rel="noopener noreferrer"
      >
        <VContentBox :key="conditie" :margin-bottom="0">
          <div class="flex items-center gap-4">
            <FontAwesomeIcon size="lg" icon="fas fa-file-pdf" class="text-red" />
            <div>{{ condities.find((x) => x.internconditienummer === conditie)?.titel }}</div>
          </div>
        </VContentBox>
      </a>
    </div>
  </div>
</template>
